import {
  css, type SerializedStyles,
} from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  type FC, type ReactNode,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  TooltipBehavior, TooltipComponent, type TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { type Theme } from '../../themes/theme.model';

type ContextMenuItemSize = 's' | 'm';

type ContextMenuDefaultItemProps = Readonly<{
  icon?: IconProp;
  children: ReactNode;
  isDisabled?: boolean;
  isDestructive?: boolean;
  onClick?: () => void;
  iconStyle?: SerializedStyles;
  itemStyle?: SerializedStyles;
  size?: ContextMenuItemSize;
  tooltipLabel?: string;
  tooltipPlacement?: TooltipPlacement;
}>;

const getItemColor = ({ isDisabled, isDestructive, theme }: { isDisabled?: boolean; isDestructive?: boolean; theme: Theme }) => {
  if (isDisabled) {
    return theme.textColors.disabled;
  }
  if (isDestructive) {
    return theme.textColors.danger;
  }

  return theme.textColors.primary;
};

const getItemPaddingForSize = (size: ContextMenuItemSize) => {
  switch (size) {
    case 's':
      return '10px 8px';
    default:
      return '16px 8px';
  }
};

const getItemFontSizeForSize = (size: ContextMenuItemSize) => {
  switch (size) {
    case 's':
      return '14px';
    default:
      return '16px';
  }
};

const itemStyle = ({ isDisabled, isDestructive, size, additionalStyle }: {
  isDisabled?: boolean;
  isDestructive?: boolean;
  size: ContextMenuItemSize;
  additionalStyle?: SerializedStyles;
}) => (theme: Theme) => css({
  cursor: isDisabled ? 'not-allowed' : 'default',
  color: getItemColor({ isDisabled, isDestructive, theme }),
  fontSize: getItemFontSizeForSize(size),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: getItemPaddingForSize(size),

  ':hover': {
    backgroundColor: theme.dropdownColors.optionBackgroundHover,
  },
}, additionalStyle);

const iconStyle = ({ isDestructive, additionalStyle }: { isDestructive?: boolean; additionalStyle?: SerializedStyles }) => (theme: Theme) => css({
  color: isDestructive ? theme.iconColors.danger : theme.iconColors.secondary,
  paddingRight: 8,
}, additionalStyle);

export const ContextMenuDefaultItem: FC<ContextMenuDefaultItemProps> = props => {
  return (
    <TooltipComponent
      tooltipContent={props.tooltipLabel}
      placement={props.tooltipPlacement}
      behavior={TooltipBehavior.ShowOnHover}
    >
      <div
        css={itemStyle({ isDisabled: props.isDisabled, isDestructive: props.isDestructive, size: props.size ?? 'm', additionalStyle: props.itemStyle })}
        onClick={props.onClick}
      >
        {props.icon && (
          <FontAwesomeIcon
            css={iconStyle({ isDestructive: props.isDestructive, additionalStyle: props.iconStyle })}
            icon={props.icon}
          />
        )}
        {props.children}
      </div>
    </TooltipComponent>
  );
};
